import { StorageError } from '../../app/errors';
import type { OurPlayerRow } from '../../types';
import { requireTransaction } from '../commit';
import { db, TABLE } from '../db';

/**
 * `ourPlayers` — our own roster (B spec §4.1, scenario 2).
 *
 * **Profile-scoped, with no cascade owner.** Deleting a match or an opponent team never touches
 * this table — the B-1 cascade-negative test pins that — and the only whole-table removal is
 * BAK-2's replace-only import (`repos/profile.ts`), which replaces it like `teams`.
 *
 * Deliberately not `players`: that table is opponent shirt-number stubs under ENV-11, keyed
 * `&[teamId+number]`. This one is the user's own free-text roster, feeds the Coach Claude
 * payload (§5.2), and references no other row.
 *
 * The B-2 `UpsertOurPlayer`/`DeleteOurPlayer` command writers land beside these in their own
 * unit; B-1 ships what the import, the archive, and the reads need.
 */

/**
 * Writes one new roster row. `add`, not `put` — BAK-2 writes into a profile the same
 * transaction has just emptied, so a colliding id means the wipe did not happen and the import
 * must abort rather than overwrite.
 *
 * Two callers, one insert (B-2): the import path above, and `UpsertOurPlayer`'s insert arm —
 * whose handler has already read the id as absent inside this same transaction, so the `add`
 * shape costs it nothing and a second insert function would be the sibling copy the reuse
 * contract's duplication gate exists to catch.
 *
 * @throws {StorageError} outside a transaction, on a duplicate id, or on a write failure.
 */
export async function insertOurPlayer(row: OurPlayerRow): Promise<void> {
  requireTransaction('insertOurPlayer');
  try {
    await db().ourPlayers.add(row);
  } catch (cause) {
    throw new StorageError('Could not save the roster player.', {
      cause,
      context: { store: TABLE.ourPlayers, id: row.id },
    });
  }
}

/**
 * The whole roster in **jersey-number order** (id tie-break) — §17.1's *"squad list, jersey
 * ascending"*, and the order §18's renderer emits player blocks in.
 *
 * ── Why the comparator and not an index ──────────────────────────────────────
 *
 * `jerseyNumber` is deliberately **not** indexed (§15.3). This is a 12–20 row table read whole by
 * every surface that touches it, so the sort is free in memory, while an index would cost a
 * re-index on every save and would change the store line — and the store line staying byte-
 * identical to v004's is what makes U-0's measured index set for the tap transaction provably
 * unable to regress.
 *
 * The Dexie read still goes through `orderBy('createdAt')` (the one index that exists) and the
 * comparator re-sorts; `id` breaks the tie because **jersey numbers are not unique** (a mid-season
 * kit change would otherwise need a temporary value), so duplicates sit adjacent and stably.
 *
 * `active` is not indexed either (no boolean key in IndexedDB); callers who want only the active
 * roster filter this handful of rows in memory.
 *
 * @throws {StorageError} on a read failure.
 */
export async function listOurPlayers(): Promise<OurPlayerRow[]> {
  try {
    const rows = await db().ourPlayers.orderBy('createdAt').toArray();
    return rows.sort((a, b) => a.jerseyNumber - b.jerseyNumber || (a.id < b.id ? -1 : 1));
  } catch (cause) {
    throw new StorageError('Could not read the roster.', {
      cause,
      context: { store: TABLE.ourPlayers },
    });
  }
}

/** @throws {StorageError} on a read failure. */
export async function getOurPlayer(id: string): Promise<OurPlayerRow | undefined> {
  try {
    return await db().ourPlayers.get(id);
  } catch (cause) {
    throw new StorageError('Could not read the roster player.', {
      cause,
      context: { store: TABLE.ourPlayers, id },
    });
  }
}

/**
 * BAK-2's replace path — the whole-roster removal the profile wipe performs. Named for its
 * single caller, like every `*ForCascade` deleter, and deliberately NOT named for the cascade:
 * no cascade reaches this table.
 *
 * @throws {StorageError} outside a transaction or on a write failure.
 */
/**
 * B-2 — the `updateOurPlayer` op's writer: the editable fields, and only those.
 *
 * The storage-layer mirror of the op's key union, the `matchNotes.updateNote` shape exactly:
 * `id` and `createdAt` are unreachable from here, so an edit cannot re-key a row or re-date one,
 * and `updatedAt` comes from the bus's clock rather than the caller's row. Widened by §15.3 from
 * three fields to six — the reshape moved what "editable" means, and this union is what keeps the
 * handler and the writer agreeing about it.
 *
 * @throws {StorageError} outside a transaction, if the player is missing, or on a write failure.
 */
export async function updateOurPlayer(
  id: string,
  changes: Partial<
    Pick<
      OurPlayerRow,
      'jerseyNumber' | 'firstName' | 'primaryPosition' | 'secondaryPositions' | 'ratings' | 'active'
    >
  >,
  updatedAt: number,
): Promise<void> {
  requireTransaction('updateOurPlayer');
  const row = await db().ourPlayers.get(id);
  if (!row) {
    throw new StorageError(`Roster player ${id} no longer exists; nothing was written.`, {
      context: { store: TABLE.ourPlayers, id },
    });
  }
  try {
    await db().ourPlayers.put({ ...row, ...changes, updatedAt });
  } catch (cause) {
    throw new StorageError('Could not update the roster player.', {
      cause,
      context: { store: TABLE.ourPlayers, id },
    });
  }
}

/**
 * B-2 — the `deleteOurPlayer` op's writer. The handler has already read the row as present
 * inside this transaction (`ERR_ROSTER_NOT_FOUND` is the renderable answer for a stale screen),
 * so this is the mechanical half; IndexedDB's `delete` of a missing key would otherwise succeed
 * silently, which is exactly why the presence check lives in the handler and not here.
 *
 * @throws {StorageError} outside a transaction or on a write failure.
 */
export async function deleteOurPlayer(id: string): Promise<void> {
  requireTransaction('deleteOurPlayer');
  try {
    await db().ourPlayers.delete(id);
  } catch (cause) {
    throw new StorageError('Could not delete the roster player.', {
      cause,
      context: { store: TABLE.ourPlayers, id },
    });
  }
}

export async function deleteOurPlayersForReplace(ids: readonly string[]): Promise<void> {
  requireTransaction('deleteOurPlayersForReplace');
  if (ids.length === 0) return;
  try {
    await db().ourPlayers.bulkDelete([...ids]);
  } catch (cause) {
    throw new StorageError('Could not clear the roster during a replace-only import.', {
      cause,
      context: { store: TABLE.ourPlayers, count: ids.length },
    });
  }
}
